import { AnimatePresence } from 'framer-motion';
import { FC, memo, useEffect, useState } from 'react';

import PandaWatermark from '@/assets/icons/cover-images/panda-logo-watermark.png';
import useAlerts from '@/hooks/useAlerts';
import {
  downloadImagesAsZip,
  downloadSelectedImagesAsZip,
  generateImagesInParallel,
  generateSingleImage,
} from '@/utils/coverImages/imageGeneration';
import { distributeTextCanvas } from '@/utils/coverImages/textDistribution';

import { ImageVariation } from '.';
import FloatingBox from '../FloatingBox';
import ImageResults from '../ImageResults';
import PostToArticleButton from '../PostToArticleButton';
import Progress from '../Progress';
import { createWatermarkTagImage } from '../shared';

interface Props {
  variations?: ImageVariation[];
  images?: string[];
  setImages: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  inArticle?: boolean;
  onReset: () => void;
}

const GeneratedImages: FC<Props> = memo(
  ({ variations, images, setImages, inArticle, onReset }) => {
    const alerts = useAlerts();

    const [selectedImages, setSelectedImages] = useState<number[]>([]);
    const [counter, setCounter] = useState(0);
    const [showProgress, setShowProgress] = useState(false);

    const generateImageVariation = async (
      variation: ImageVariation,
    ): Promise<string | undefined> => {
      try {
        return await generateSingleImage(
          () => {
            const imageHolder = document.createElement('div');
            imageHolder.classList.add('image-holder-var19');

            const leftImage = document.createElement('div');
            leftImage.classList.add('image-var19');
            leftImage.style.backgroundImage = `url(${variation.leftImageSrc})`;

            const rightImage = document.createElement('div');
            rightImage.classList.add('image-var19');
            rightImage.style.backgroundImage = `url(${variation.rightImageSrc})`;

            const fullImage = document.createElement('div');
            fullImage.classList.add('full-image-var19');
            fullImage.style.backgroundImage = `url(${variation.bottomImageSrc})`;

            if (variation.bottomText) {
              const bottomText = document.createElement('div');
              const bottomTextP = document.createElement('p');
              bottomText.classList.add('bottom-text-var19');
              let text = variation.bottomText;
              if (variation.bottomTextBrakes) {
                bottomTextP.innerText = text;
              } else {
                text = distributeTextCanvas(text, {
                  maxWidth: 900,
                  fontFamily: 'SourceSansProBold',
                  minFontSize:
                    variation.bottomFontSize !== 0
                      ? variation.bottomFontSize
                      : 62,
                });
                bottomTextP.innerHTML = text;
              }
              bottomText.appendChild(bottomTextP);
              imageHolder.appendChild(bottomText);

              if (variation.bottomFontSize) {
                bottomText.style.fontSize = `${variation.bottomFontSize}px`;
              }
              bottomText.style.color = variation.bottomTextColor;
              bottomTextP.style.boxShadow = `-15px 15px 0px ${variation.bottomTextColor}`;
            }

            imageHolder.appendChild(leftImage);

            const leftWatermark = createWatermarkTagImage(
              'left-actual-photo-var19',
              variation.leftWatermarkTag,
              variation.leftWatermarkPosition,
            );
            if (leftWatermark) leftImage.appendChild(leftWatermark);
            imageHolder.appendChild(rightImage);

            const rightWatermark = createWatermarkTagImage(
              'right-actual-photo-var19',
              variation.rightWatermarkTag,
              variation.rightWatermarkPosition,
            );
            if (rightWatermark) rightImage.appendChild(rightWatermark);
            imageHolder.appendChild(fullImage);

            const bottomWatermark = createWatermarkTagImage(
              'bottom-actual-photo-var19',
              variation.bottomWatermarkTag,
              variation.bottomWatermarkPosition,
            );
            if (bottomWatermark) fullImage.appendChild(bottomWatermark);

            if (variation.watermark) {
              const pandaWatermark = new Image();
              pandaWatermark.classList.add('watermark-var19');
              pandaWatermark.src = PandaWatermark;
              imageHolder.appendChild(pandaWatermark);
            }

            return imageHolder;
          },
          {
            holderClassName: 'generated-images-holder-var19',
            pixelRatio: 1,
          },
        );
      } catch (e) {
        alerts.error();
        return undefined;
      }
    };

    const generateAllVariations = async () => {
      if (!variations?.length) return;

      setShowProgress(true);
      setCounter(0);

      try {
        const validImages = await generateImagesInParallel(
          variations,
          generateImageVariation,
          (current) => {
            setCounter(current);
          },
        );

        setImages(validImages);
      } finally {
        setShowProgress(false);
        setCounter(0);
      }
    };

    const handleDownloadZip = async (selected: boolean) => {
      if (!images?.length) return;

      if (selected) {
        await downloadSelectedImagesAsZip(images, selectedImages, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      } else {
        await downloadImagesAsZip(images, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      }
    };

    useEffect(() => {
      generateAllVariations();
      // eslint-disable-next-line
    }, [variations]);

    useEffect(() => {
      if (!images?.length) return;
      setSelectedImages(images?.map((_, index) => index));
    }, [images]);

    return (
      <>
        <ImageResults
          images={images}
          selectedImages={selectedImages}
          setSelectedImages={setSelectedImages}
          setImages={setImages}
        />
        <AnimatePresence>
          {!!showProgress && !!variations?.length && (
            <Progress
              key={1}
              progressPercent={(counter / variations.length) * 100}
            />
          )}
          {!inArticle ? (
            <>
              {!!images?.length && (
                <FloatingBox
                  key={2}
                  selectedImages={selectedImages}
                  unselectAll={() => setSelectedImages([])}
                  selectAll={() =>
                    setSelectedImages(images?.map((_, index) => index))
                  }
                  onDownload={handleDownloadZip}
                  images={images}
                />
              )}
            </>
          ) : (
            <>
              {!!images?.length && (
                <PostToArticleButton
                  images={images}
                  selectedImages={selectedImages}
                  onReset={onReset}
                />
              )}
            </>
          )}
        </AnimatePresence>
      </>
    );
  },
);

export default GeneratedImages;
